import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { PrivacyProvider } from './contexts/PrivacyContext';
import MainLayout from './components/layout/MainLayout';
import { AdminRoute, ManagerRoute } from './components/auth/ProtectedRoutes';
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import LeadsPage from './pages/leads/LeadsPage';
import LeadDetailPage from './pages/leads/LeadDetailPage';
import DemosPage from './pages/demos/DemosPage';
import DealsPage from './pages/deals/DealsPage';
import DealDetailPage from './pages/deals/DealDetailPage';
import TeamPage from './pages/team/TeamPage';
import UserProfilePage from './pages/team/UserProfilePage';
import UserActivityPage from './pages/team/UserActivityPage';
import IncentivesPage from './pages/incentives/IncentivesPage';
import ReportsPage from './pages/reports/ReportsPage';
import SettingsPage from './pages/settings/SettingsPage';
import ClientsPage from './pages/clients/ClientsPage';
import ClientDetailPage from './pages/clients/ClientDetailPage';
import SSOPage from './pages/admin/SSOPage';
import WhatsAppAIPage from './pages/admin/WhatsAppAIPage';
import WACampaignsPage from './pages/admin/WACampaignsPage';
import MasterLeadDataPage from './pages/admin/MasterLeadDataPage';
import PlansPage from './pages/admin/PlansPage';
import InvoicesPage from './pages/admin/InvoicesPage';
import IncentiveConfigPage from './pages/admin/IncentiveConfigPage';
import ApiConfigPage from './pages/admin/ApiConfigPage';
import WebhooksPage from './pages/admin/WebhooksPage';
import EmailConfigPage from './pages/admin/EmailConfigPage';
import LeadBankPage from './pages/admin/LeadBankPage';
import TagsManagementPage from './pages/admin/TagsManagementPage';
import WhatsAppTemplatesPage from './pages/admin/WhatsAppTemplatesPage';
import { useDynamicFavicon } from './components/ui/DynamicLogo';
import './App.css';

// Component to set dynamic favicon
const DynamicFaviconSetter = () => {
  useDynamicFavicon();
  return null;
};

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <PrivacyProvider>
      <DynamicFaviconSetter />
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          
          {/* Protected Routes */}
          <Route element={<MainLayout />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/leads" element={<LeadsPage />} />
            <Route path="/leads/:id" element={<LeadDetailPage />} />
            <Route path="/demos" element={<DemosPage />} />
            <Route path="/deals" element={<DealsPage />} />
            <Route path="/deals/:id" element={<DealDetailPage />} />
            <Route path="/team" element={<ManagerRoute><TeamPage /></ManagerRoute>} />
            <Route path="/team/:id/profile" element={<ManagerRoute><UserProfilePage /></ManagerRoute>} />
            <Route path="/team/:id/activity" element={<ManagerRoute><UserActivityPage /></ManagerRoute>} />

            <Route path="/clients" element={<ClientsPage />} />
            <Route path="/clients/:id" element={<ClientDetailPage />} />
            <Route path="/incentives" element={<IncentivesPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/profile" element={<UserProfilePage />} />
            <Route path="/profile/activity" element={<ManagerRoute><UserActivityPage /></ManagerRoute>} />
            
            {/* Admin Only Routes */}
            <Route path="/admin/users" element={<AdminRoute><TeamPage /></AdminRoute>} />
            <Route path="/admin/plans" element={<AdminRoute><PlansPage /></AdminRoute>} />
            <Route path="/admin/invoices" element={<AdminRoute><InvoicesPage /></AdminRoute>} />
            <Route path="/admin/incentive-config" element={<AdminRoute><IncentiveConfigPage /></AdminRoute>} />
            <Route path="/admin/api-config" element={<AdminRoute><ApiConfigPage /></AdminRoute>} />
            <Route path="/admin/webhooks" element={<AdminRoute><WebhooksPage /></AdminRoute>} />
            <Route path="/admin/email-config" element={<AdminRoute><EmailConfigPage /></AdminRoute>} />
            <Route path="/admin/sso" element={<AdminRoute><SSOPage /></AdminRoute>} />
            <Route path="/admin/whatsapp-ai" element={<AdminRoute><WhatsAppAIPage /></AdminRoute>} />
            <Route path="/admin/wa-campaigns" element={<AdminRoute><WACampaignsPage /></AdminRoute>} />
            <Route path="/admin/master-lead-data" element={<AdminRoute><MasterLeadDataPage /></AdminRoute>} />

            <Route path="/admin/lead-bank" element={<AdminRoute><LeadBankPage /></AdminRoute>} />
            <Route path="/admin/tags" element={<AdminRoute><TagsManagementPage /></AdminRoute>} />
            <Route path="/admin/whatsapp-templates" element={<AdminRoute><WhatsAppTemplatesPage /></AdminRoute>} />
            <Route path="/admin/settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />
          </Route>
          
          {/* Redirects */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      </PrivacyProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
